import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ChevronLeft, Save, Send, Image, X, Link as LinkIcon, MoreVertical, Archive, Trash2 } from 'lucide-react'
import clsx from 'clsx'
import axios from 'axios'
import { buildApiUrl } from '../../../config/api'
import { toast } from '../../../components/Toast'
import { Button, Card, Badge, Input, Textarea, Select, MultiSelect, DeleteConfirmModal, Modal, ModalHeader, ModalBody, ModalFooter } from '../../../components/ui'
import RichTextEditor from '../../../components/RichTextEditor'

interface Category { id: number; name: string; slug: string; is_active: boolean }
interface Tag { id: number; name: string; slug: string }
interface MediaItem { id: number; file_url: string; file_name: string }

export default function BlogPostEditorPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEditing = !!id
  const [loading, setLoading] = useState(isEditing)
  const [saving, setSaving] = useState(false)
  const [categories, setCategories] = useState<Category[]>([])
  const [tags, setTags] = useState<Tag[]>([])
  const [statusCode, setStatusCode] = useState('DRAFT')
  const [statusLabel, setStatusLabel] = useState('Rascunho')
  const [uploadingCover, setUploadingCover] = useState(false)
  const [postMedia, setPostMedia] = useState<MediaItem[]>([])
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [slug, setSlug] = useState('')
  const [actionsOpen, setActionsOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [archiveModalOpen, setArchiveModalOpen] = useState(false)
  const [uploadModalOpen, setUploadModalOpen] = useState(false)
  const [uploadPreview, setUploadPreview] = useState('')
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadAlt, setUploadAlt] = useState('')

  const [form, setForm] = useState({
    title: '', excerpt: '', content: '', category_id: '',
    cover_image_url: '', cover_image_alt: '',
    is_featured: false, is_pinned: false,
    seo_title: '', seo_description: '', og_image_url: '',
    tag_ids: [] as string[],
  })

  useEffect(() => {
    Promise.all([
      axios.get(buildApiUrl('/v1/posts/categories')),
      axios.get(buildApiUrl('/v1/posts/tags')),
    ]).then(([catRes, tagRes]) => {
      setCategories(catRes.data.data)
      setTags(tagRes.data.data)
    }).catch(() => {})

    if (isEditing) {
      axios.get(buildApiUrl(`/v1/posts/${id}`)).then(res => {
        const p = res.data.data.post
        const postTags = res.data.data.tags || []
        setForm({
          title: p.title || '', excerpt: p.excerpt || '', content: p.content || '',
          category_id: p.category_id?.toString() || '',
          cover_image_url: p.cover_image_url || '', cover_image_alt: p.cover_image_alt || '',
          is_featured: !!p.is_featured, is_pinned: !!p.is_pinned,
          seo_title: p.seo_title || '', seo_description: p.seo_description || '',
          og_image_url: p.og_image_url || '',
          tag_ids: postTags.map((t: Tag) => String(t.id)),
        })
        setSlug(p.slug || '')
        setStatusCode(p.status_code)
        setStatusLabel(p.status_label)
        setPostMedia(res.data.data.media || [])
      }).catch(() => toast('error', 'Erro ao carregar post')).finally(() => setLoading(false))
    }
  }, [id])

  // Auto-gerar slug do título
  useEffect(() => {
    if (!isEditing && form.title) {
      setSlug(form.title.toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''))
    }
  }, [form.title, isEditing])

  function validate(): boolean {
    const e: Record<string, string> = {}
    if (!form.title.trim()) e.title = 'Título é obrigatório'
    if (!form.content.trim() || form.content === '<p></p>') e.content = 'Conteúdo é obrigatório'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  async function handleSave(publish = false) {
    if (saving) return
    if (!validate()) return
    setSaving(true)
    try {
      const payload = {
        ...form,
        category_id: form.category_id ? Number(form.category_id) : null,
        tag_ids: form.tag_ids.map(Number),
      }

      if (isEditing) {
        await axios.put(buildApiUrl(`/v1/posts/${id}`), payload)
        if (publish) await axios.put(buildApiUrl(`/v1/posts/${id}/publish`))
        toast('success', publish ? 'Post publicado' : 'Post atualizado')
        // Reload para pegar slug atualizado
        const res = await axios.get(buildApiUrl(`/v1/posts/${id}`))
        setSlug(res.data.data.post.slug)
        setStatusCode(res.data.data.post.status_code)
        setStatusLabel(res.data.data.post.status_label)
      } else {
        const res = await axios.post(buildApiUrl('/v1/posts'), payload)
        const newId = res.data.data.id
        setSlug(res.data.data.slug)
        if (publish) {
          await axios.put(buildApiUrl(`/v1/posts/${newId}/publish`))
          setStatusCode('PUBLISHED')
          setStatusLabel('Publicado')
        }
        toast('success', publish ? 'Post publicado' : 'Rascunho salvo')
        navigate(`/blog/posts/${newId}/edit`, { replace: true })
      }
    } catch (err: any) {
      toast('error', err.response?.data?.error || 'Erro ao salvar')
    } finally { setSaving(false) }
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadFile(file)
    setUploadPreview(URL.createObjectURL(file))
    setUploadAlt('')
    setUploadModalOpen(true)
    e.target.value = ''
  }

  async function handleConfirmUpload() {
    if (!uploadFile || uploadingCover) return
    setUploadingCover(true)
    try {
      const formData = new FormData()
      formData.append('file', uploadFile)
      formData.append('alt_text', uploadAlt)
      if (id) formData.append('post_id', id)
      const res = await axios.post(buildApiUrl('/v1/posts/media'), formData)
      const fileUrl = res.data.data.file_url
      setForm(f => ({ ...f, cover_image_url: fileUrl, cover_image_alt: uploadAlt }))
      setPostMedia(prev => [...prev, { id: res.data.data.id, file_url: fileUrl, file_name: uploadFile.name }])
      toast('success', 'Imagem enviada')
      setUploadModalOpen(false)
      setUploadFile(null)
      setUploadPreview('')
    } catch { toast('error', 'Erro no upload') } finally { setUploadingCover(false) }
  }

  async function handleArchive() {
    if (!id) return
    await axios.put(buildApiUrl(`/v1/posts/${id}/archive`))
    toast('success', 'Post arquivado')
    setStatusCode('ARCHIVED')
    setStatusLabel('Arquivado')
    setActionsOpen(false)
  }

  async function handleDelete() {
    if (!id) return
    await axios.delete(buildApiUrl(`/v1/posts/${id}`))
    toast('success', 'Post excluído')
    navigate('/blog/posts')
  }

  const statusVariant: Record<string, 'success' | 'warning' | 'gray'> = {
    PUBLISHED: 'success', SCHEDULED: 'warning', DRAFT: 'gray', ARCHIVED: 'gray',
  }

  if (loading) return (
    <div className="space-y-6 animate-pulse">
      <div className="h-9 w-48 bg-gray-200 rounded" />
      <Card><div className="h-11 w-full bg-gray-100 rounded-lg" /></Card>
      <Card><div className="h-64 w-full bg-gray-100 rounded-lg" /></Card>
    </div>
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/blog/posts')} className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-gray-200/60 text-gray-400 hover:text-gray-600 transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">{isEditing ? 'Editar Post' : 'Novo Post'}</h1>
          {isEditing && <Badge variant={statusVariant[statusCode] || 'gray'} size="sm">{statusLabel}</Badge>}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => handleSave(false)} loading={saving}>
            <Save className="w-4 h-4 mr-1.5" />{isEditing ? 'Salvar' : 'Salvar rascunho'}
          </Button>
          {statusCode !== 'PUBLISHED' && (
            <Button onClick={() => handleSave(true)} loading={saving}>
              <Send className="w-4 h-4 mr-1.5" />Publicar
            </Button>
          )}
          {isEditing && (
            <div className="relative">
              <button onClick={() => setActionsOpen(!actionsOpen)}
                className="h-10 w-10 flex items-center justify-center rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-50 transition-colors">
                <MoreVertical className="w-4 h-4" />
              </button>
              {actionsOpen && (<>
                <div className="fixed inset-0 z-10" onClick={() => setActionsOpen(false)} />
                <div className="absolute right-0 top-11 z-20 w-44 bg-white rounded-xl shadow-lg border border-gray-200 py-1">
                  {statusCode !== 'ARCHIVED' && (
                    <button onClick={() => { setActionsOpen(false); setArchiveModalOpen(true) }} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                      <Archive className="w-3.5 h-3.5 text-gray-400" /> Arquivar
                    </button>
                  )}
                  <button onClick={() => { setActionsOpen(false); setDeleteModalOpen(true) }} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-error-500 hover:bg-error-50">
                    <Trash2 className="w-3.5 h-3.5" /> Excluir
                  </button>
                </div>
              </>)}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Conteúdo principal */}
        <div className="lg:col-span-2 space-y-6">
          {/* Título e Slug */}
          <Card>
            <div className="space-y-4">
              <Input label="Título" value={form.title}
                onChange={e => { setForm(f => ({ ...f, title: e.target.value })); setErrors(er => ({ ...er, title: '' })) }}
                placeholder="Título do post" error={errors.title} />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Slug (URL)</label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-11 flex items-center px-4 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-600">
                    <LinkIcon className="w-3.5 h-3.5 text-gray-400 mr-2 flex-shrink-0" />
                    <span className="truncate">/{slug || '...'}</span>
                  </div>
                </div>
              </div>

              <Textarea label="Resumo" value={form.excerpt}
                onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))}
                placeholder="Breve resumo para listagem e SEO..." rows={2} />
            </div>
          </Card>

          {/* Conteúdo */}
          <Card>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Conteúdo</label>
            <RichTextEditor
              value={form.content}
              onChange={content => { setForm(f => ({ ...f, content })); setErrors(er => ({ ...er, content: '' })) }}
              error={errors.content}
              placeholder="Escreva o conteúdo do post..."
            />
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Imagens */}
          <Card>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Imagens</h3>
              <label className="text-xs text-brand-500 hover:text-brand-600 font-medium cursor-pointer">
                + Adicionar
                <input type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
              </label>
            </div>

            {/* Capa atual */}
            {form.cover_image_url && (
              <div className="relative mb-3">
                <img src={form.cover_image_url} alt={form.cover_image_alt} className="w-full h-40 rounded-lg object-cover" />
                <button onClick={() => setForm(f => ({ ...f, cover_image_url: '', cover_image_alt: '' }))}
                  className="absolute top-2 right-2 h-7 w-7 bg-white/90 rounded-full flex items-center justify-center text-gray-500 hover:text-error-500">
                  <X className="w-4 h-4" />
                </button>
                <span className="absolute bottom-2 left-2 text-[10px] font-bold text-white bg-brand-500 px-1.5 py-0.5 rounded">CAPA</span>
              </div>
            )}

            {/* Grid de mídias */}
            {postMedia.length > 0 ? (
              <>
                <div className="grid grid-cols-3 gap-2">
                  {postMedia.map(m => (
                    <button key={m.id} onClick={() => setForm(f => ({ ...f, cover_image_url: m.file_url }))}
                      className={`relative rounded-lg overflow-hidden border-2 transition-colors ${form.cover_image_url === m.file_url ? 'border-brand-500' : 'border-transparent hover:border-gray-300'}`}>
                      <img src={m.file_url} alt={m.file_name} className="aspect-square object-cover w-full" />
                      {form.cover_image_url === m.file_url && (
                        <div className="absolute inset-0 bg-brand-500/20 flex items-center justify-center">
                          <span className="text-[10px] font-bold text-white bg-brand-500 px-1.5 py-0.5 rounded">CAPA</span>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-gray-400 mt-2">Clique numa imagem para definir como capa</p>
              </>
            ) : !form.cover_image_url && (
              <label className="flex flex-col items-center justify-center h-28 border-2 border-dashed border-gray-200 rounded-lg cursor-pointer hover:border-brand-300 transition-colors">
                <Image className="w-7 h-7 text-gray-300 mb-1.5" />
                <span className="text-xs text-gray-400">Clique para enviar</span>
                <input type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
              </label>
            )}
          </Card>

          {/* Categoria */}
          <Card>
            <Select label="Categoria" value={form.category_id}
              onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))}
              options={categories.filter(c => c.is_active).map(c => ({ value: String(c.id), label: c.name }))}
              placeholder="Sem categoria" />
          </Card>

          {/* Tags */}
          <Card>
            <MultiSelect label="Tags"
              options={tags.map(t => ({ value: String(t.id), label: t.name }))}
              selected={form.tag_ids}
              onChange={ids => setForm(f => ({ ...f, tag_ids: ids }))}
              placeholder="Selecione tags..." />
          </Card>

          {/* Opções */}
          <Card>
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Opções</h3>
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={form.is_featured}
                  onChange={e => setForm(f => ({ ...f, is_featured: e.target.checked }))}
                  className="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500" />
                <span className="text-sm text-gray-700">Destaque</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={form.is_pinned}
                  onChange={e => setForm(f => ({ ...f, is_pinned: e.target.checked }))}
                  className="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500" />
                <span className="text-sm text-gray-700">Fixar no topo</span>
              </label>
            </div>
          </Card>

          {/* SEO */}
          <Card>
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">SEO</h3>
            <div className="space-y-3">
              <Input label="Título SEO" value={form.seo_title}
                onChange={e => setForm(f => ({ ...f, seo_title: e.target.value }))}
                placeholder="Se diferente do título" />
              <Textarea label="Meta description" value={form.seo_description}
                onChange={e => setForm(f => ({ ...f, seo_description: e.target.value }))}
                placeholder="Resumo para mecanismos de busca" rows={2} />
            </div>
          </Card>
        </div>
      </div>

      {/* Modal upload de imagem */}
      <Modal open={uploadModalOpen} onClose={() => { setUploadModalOpen(false); setUploadFile(null); setUploadPreview('') }} size="sm">
        <ModalHeader onClose={() => { setUploadModalOpen(false); setUploadFile(null); setUploadPreview('') }}>Adicionar imagem</ModalHeader>
        <ModalBody>
          <div className="space-y-4">
            {uploadPreview && (
              <img src={uploadPreview} alt="Preview" className="w-full h-48 rounded-lg object-cover" />
            )}
            <Input label="Texto alternativo (alt)" value={uploadAlt}
              onChange={e => setUploadAlt(e.target.value)}
              placeholder="Descreva a imagem para acessibilidade e SEO" />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => { setUploadModalOpen(false); setUploadFile(null); setUploadPreview('') }}>Cancelar</Button>
          <Button onClick={handleConfirmUpload} loading={uploadingCover}>Adicionar</Button>
        </ModalFooter>
      </Modal>

      <DeleteConfirmModal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} onConfirm={handleDelete}
        title="Excluir post" message={<>Tem certeza que deseja excluir o post <strong>"{form.title}"</strong>? Esta ação não pode ser desfeita.</>} confirmLabel="Excluir" />

      <DeleteConfirmModal open={archiveModalOpen} onClose={() => setArchiveModalOpen(false)} onConfirm={handleArchive}
        title="Arquivar post" message={<>Tem certeza que deseja arquivar o post <strong>"{form.title}"</strong>? Ele não será mais exibido publicamente.</>} confirmLabel="Arquivar" />
    </div>
  )
}
